import { Badge, Button, Card, Spinner } from '@ohear/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ExportButtons, type ExportItem } from '../../../features/exports'
import { CharacterTable } from '../../../features/supervisor/CharacterTable'
import { useRenderAllStems } from '../../../lib/api/stems'
import { useProjectOverview, useReviewQueue } from '../../../lib/api/supervisor'
import { useRequireRole } from '../../../lib/auth'
import { useRealtimeChannel } from '../../../lib/realtime/use-channel'

export const Route = createFileRoute('/supervisor/projects/$projectId/')({
  component: SupervisorProjectDetail,
})

function SupervisorProjectDetail() {
  useRequireRole(['supervisor'])
  const { projectId } = Route.useParams()
  const overview = useProjectOverview(projectId)
  const recent = useReviewQueue(projectId)
  const renderAll = useRenderAllStems(projectId)

  // Realtime: subscribe to org takes + project_progress topics. The
  // generalised hook fans events through both Postgres replication on
  // domain_events and ad-hoc broadcasts; we only care about the cache
  // invalidation here so the overview cards stay current.
  const orgId = (overview.data as { organization_id?: string } | undefined)?.organization_id ?? null
  useRealtimeChannel({
    orgId,
    topic: 'takes',
    invalidateKeys: [
      ['supervisor', 'overview', projectId],
      ['supervisor', 'queue', projectId],
    ],
  })
  useRealtimeChannel({
    orgId,
    topic: 'project_progress',
    invalidateKeys: [['supervisor', 'overview', projectId]],
  })

  if (overview.isLoading) {
    return (
      <div className="flex items-center gap-2 text-[var(--text-2)]">
        <Spinner /> <span>Loading project...</span>
      </div>
    )
  }

  if (overview.error || !overview.data) {
    return (
      <Card>
        <p className="m-0 text-[var(--red)]">
          Couldn't load project.{' '}
          {overview.error instanceof Error ? overview.error.message : 'Unknown error'}
        </p>
      </Card>
    )
  }

  const o = overview.data
  const pct = o.total_cues === 0 ? 0 : Math.round((o.approved / o.total_cues) * 100)
  const recentTakes = recent.data?.takes.slice(0, 10) ?? []

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3 pb-4 border-b border-[var(--border)]">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)] truncate">
            {o.organization_name}
          </div>
          <h1 className="text-[22px] font-semibold tracking-tight m-0 truncate text-[var(--text)]">
            {o.project_name}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/supervisor/projects/$projectId/takes"
            params={{ projectId }}
            className="no-underline"
          >
            <Button variant="ghost">Review queue ({o.pending_supervisor})</Button>
          </Link>
          <Link
            to="/supervisor/projects/$projectId/stems"
            params={{ projectId }}
            className="no-underline"
          >
            <Button variant="ghost">Stems</Button>
          </Link>
          <Button
            variant="primary"
            loading={renderAll.isPending}
            disabled={o.approved === 0 || renderAll.isPending}
            onClick={() => renderAll.mutate()}
          >
            Render all stems
          </Button>
        </div>
      </header>

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}
      >
        <SummaryCard label="Total cues" value={o.total_cues} />
        <SummaryCard label="Approved" value={`${o.approved} (${pct}%)`} tone="green" />
        <SummaryCard label="Pending review" value={o.pending_supervisor} tone="amber" />
        <SummaryCard label="Pickups out" value={o.pending_pickup} tone="amber" />
      </div>

      <Card title="Characters" actions={<ExportButtons exports={qcReportExports(o.characters)} />}>
        <CharacterTable projectId={projectId} characters={o.characters} />
      </Card>

      <Card
        title="Recent takes"
        actions={
          <Link
            to="/supervisor/projects/$projectId/takes"
            params={{ projectId }}
            className="no-underline"
          >
            <Button size="sm" variant="ghost">
              Open queue
            </Button>
          </Link>
        }
      >
        {recent.isLoading ? (
          <div className="flex items-center gap-2 text-[var(--text-2)]">
            <Spinner /> Loading recent takes...
          </div>
        ) : recentTakes.length === 0 ? (
          <p className="text-[13px] text-[var(--text-2)] m-0">
            Nothing waiting on you. Great work.
          </p>
        ) : (
          <ul className="m-0 p-0 list-none flex flex-col gap-1">
            {recentTakes.map((t) => (
              <li
                key={t.take_id}
                className="flex items-center gap-2 border-b border-[var(--border)] py-1.5 last:border-b-0 text-[13px]"
              >
                <Badge variant="gray">{t.character_code}</Badge>
                <span className="font-medium">{t.character_display_name}</span>
                <span className="text-[var(--text-2)]">cue {t.cue_number}</span>
                <span className="text-[var(--text-2)] truncate flex-1 min-w-0">{t.cue_text}</span>
                <span className="text-[12px] text-[var(--text-2)] tabular-nums whitespace-nowrap">
                  {t.duration_sec.toFixed(1)}s
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  )
}

function qcReportExports(
  characters: readonly import('../../../lib/api/supervisor').SupervisorCharacterRow[],
): ExportItem[] {
  return characters
    .filter((c) => c.latest_stem_version != null && c.latest_stem_version > 0)
    .map((c) => ({
      label: `QC ${c.character_display_name} v${c.latest_stem_version}`,
      format: 'pdf' as const,
      endpoint: '/api/v1/exports/qc-report',
      params: {
        character_id: c.character_id,
        version: c.latest_stem_version as number,
      },
    }))
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string
  value: string | number
  tone?: 'green' | 'amber'
}) {
  const color =
    tone === 'green'
      ? 'text-[var(--green)]'
      : tone === 'amber'
        ? 'text-[var(--amber)]'
        : 'text-[var(--text)]'
  return (
    <Card>
      <div className="text-[11px] uppercase tracking-wide text-[var(--text-3)]">{label}</div>
      <div className={`mt-0.5 text-[22px] font-semibold tabular-nums leading-tight ${color}`}>
        {value}
      </div>
    </Card>
  )
}
